require('./RAObjectiveDetails.scss');

// @vendors
const React = require('react');
const PropTypes = require('prop-types');
const moment = require('moment');
const classNames = require('classnames');

// @components
const { LoadingSection } = require('commonsComponents/loadingSection');
const RAOneLineSymbolAmount = require('../commons/RAOneLineSymbolAmount/RAOneLineSymbolAmount');
const RAServiceCostModal = require('../RAServiceCostModal/RAServiceCostModal');
const RAProbabilityTag = require('../RAProbabilityTag/RAProbabilityTag');

// @commons
const { ContentBox } = require('commonsComponents/contentBox');
const { OKButton } = require('components/commons/OKButton/OKButton');
const { OKLink } = require('commonsComponents/OKLink');
const { AmountInput } = require('commonsComponents/amountInput');
const OKModal = require('components/commons/OKModal/OKModal');

// @constants
const {
    EURO_TEXT,
    EURO_SYMBOL,
    PERCENTAGE_SYMBOL,
} = require('constants/index');
const {
    DATE_FORMAT,
    RA_GOAL_REACHED,
    RA_GOAL_NOT_REACHED,
    RA_SET_GOAL_MINIMUM_INVESTMENT_AMOUNT,
    RA_SET_GOAL_MAXIMUM_INVESTMENT_AMOUNT,
    PROC_RA_OBJECTIVE_TRACKING_INTERACTION,
    PROC_RA_OBJECTIVE_TRACKING_EDIT_IMPORT_AMOUNT,
    PROC_RA_OBJECTIVE_TRACKING,
    PROC_RA_OBJECTIVE_DETAILS_AMOUNT_MODAL,
    PROC_RA_OBJECTIVE_TRACKING_PROB_REACH_OBJECTIVE,
    PROC_RA_CONFIRM,
    PROC_RA_VIEW,
    PROC_RA_CLOSE,
} = require('../../raConstants/index');

// @utilites
const { formatText, FormattedText } = require('core/i18n').i18n;
const {
    formatPercentage,
    formatWithoutRoundedPercentage,
} = require('../../utilities/RACurrencyHelper');
const { formatAmount } = require('utilities/currencyHelper');
const DateHelper = require('utilities/dateHelper');
const { recordLink } = require('utilities/tagging');

class RAObjectiveDetails extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            isModalOpen: false,
        };
        this.handleCloseButtonClick = this.handleCloseButtonClick.bind(this);
        this.handleOnLinkClick = this.handleOnLinkClick.bind(this);
        this.handleCloseModal = this.handleCloseModal.bind(this);
        this.handleOnEditObjectiveAmount = this.handleOnEditObjectiveAmount.bind(
            this,
        );
        this.handleOnChangeObjectiveAmount = this.handleOnChangeObjectiveAmount.bind(
            this,
        );
        this.handleOnProbabilityAnchorClick = this.handleOnProbabilityAnchorClick.bind(
            this,
        );
    }

    handleOnProbabilityAnchorClick() {
        const { onProbabilityAnchorClick } = this.props;
        recordLink(
            PROC_RA_OBJECTIVE_TRACKING_INTERACTION,
            PROC_RA_OBJECTIVE_TRACKING_PROB_REACH_OBJECTIVE,
        );
        onProbabilityAnchorClick();
    }

    handleOnEditObjectiveAmount() {
        recordLink(
            PROC_RA_OBJECTIVE_TRACKING_INTERACTION,
            PROC_RA_OBJECTIVE_TRACKING_EDIT_IMPORT_AMOUNT,
        );
        recordLink(
            PROC_RA_OBJECTIVE_TRACKING,
            PROC_RA_OBJECTIVE_DETAILS_AMOUNT_MODAL,
            PROC_RA_VIEW,
        );
        this.props.showEditAmountModal(this.props.objectiveAmount);
    }

    handleOnChangeObjectiveAmount() {
        recordLink(
            PROC_RA_OBJECTIVE_TRACKING,
            PROC_RA_OBJECTIVE_DETAILS_AMOUNT_MODAL,
            PROC_RA_CONFIRM,
        );
        const paramPortfolioId = this.props.portfolioId;
        const amount = this.props.immEditAmountModal.get('resetAmount');
        this.props.updateObjectiveName(paramPortfolioId, null, amount);
        this.props.hideEditAmountModal();
    }

    buildAssociatedPortfolioDetail() {
        const { displayStrategyDetail } = this.props;
        if (displayStrategyDetail) {
            const { productName, productIcon } = this.props;
            return (
                <div className="ra-objective-details__portfolio-details">
                    <div className="ra-objective-details__portfolio-icon">
                        <div className={productIcon} />
                    </div>
                    <div className="ra-objective-details__portfolio-summary">
                        <p className="ra-objective-details__portfolio-name">
                            {productName}
                        </p>
                        <p className="ra-objective-details__portfolio-text">
                            <FormattedText value="raObjectiveTracking-raObjectiveTracking_strategyText" />
                        </p>
                    </div>
                </div>
            );
        }
    }

    buildProgressContent() {
        const {
            actualAmount,
            displayStrategyDetail,
            objectiveAmount,
            goalProgress,
            objectiveEndDate,
        } = this.props;
        const progressPercentageWithSymbol = formatPercentage(
            goalProgress,
            true,
            false,
        );
        const progressStyleBar =
            goalProgress <= 1
                ? progressPercentageWithSymbol
                : formatPercentage(1, true);
        const progressSuccessStylebar =
            goalProgress > 1 && goalProgress <= 2
                ? formatPercentage(goalProgress - 1, true)
                : formatPercentage(1, true);
        const associatedPortfolioContent = this.buildAssociatedPortfolioDetail();
        let successContent, editObjectiveButton;
        if (goalProgress > 1) {
            successContent = (
                <div className="ra-objective-details__progress-success-track">
                    <div
                        className="ra-objective-details__progress-success-bar"
                        style={{ width: progressSuccessStylebar }}
                    ></div>
                </div>
            );
        }

        if (!displayStrategyDetail) {
            const goalReached = goalProgress >= 1;
            const isGoalNotReached =
                !goalReached &&
                moment(objectiveEndDate).isSameOrBefore(moment(), 'day');
            editObjectiveButton = (
                <OKButton
                    modifier="ghost"
                    size="small"
                    leftIcon="icon-editar"
                    onClick={this.handleOnEditObjectiveAmount}
                    disabled={isGoalNotReached}
                >
                    <FormattedText value="raObjectiveTracking-detailEditObjectiveButton" />
                </OKButton>
            );
        }

        const actualAmountFormatted = {
            amount: actualAmount,
            currency: EURO_TEXT,
        };
        const objectiveAmountFormatted = {
            amount: objectiveAmount,
            currency: EURO_TEXT,
        };
        return (
            <div className="ra-objective-details__progress">
                {associatedPortfolioContent}
                <div className="ra-objective-details__summary">
                    <FormattedText
                        value="raObjectiveTracking-detailProgressSummary"
                        injectedStrings={[progressPercentageWithSymbol]}
                    />
                </div>
                {successContent}
                <div className="ra-objective-details__progress-track">
                    <div
                        className="ra-objective-details__progress-bar"
                        style={{ width: progressStyleBar }}
                    ></div>
                </div>
                <div className="ra-objective-details__progress-values">
                    <div
                        className="ra-objective-details__progress-actual"
                        aria-labeledby="ra-objective-details__progress-actual-label"
                    >
                        <RAOneLineSymbolAmount
                            amountObject={actualAmountFormatted}
                        />
                    </div>
                    <div
                        className="ra-objective-details__progress-objective"
                        aria-labeledby="ra-objective-details__progress-objective-label"
                    >
                        <RAOneLineSymbolAmount
                            amountObject={objectiveAmountFormatted}
                        />
                    </div>
                </div>
                <div className="ra-objective-details__progress-labels">
                    <div
                        id="ra-objective-details__progress-actual-label"
                        className="ra-objective-details__progress-actual-label"
                    >
                        <FormattedText value="raObjectiveTracking-detailProgressActualLabel" />
                    </div>
                    <div
                        id="ra-objective-details__progress-objective-label"
                        className="ra-objective-details__progress-objective-label"
                    >
                        <FormattedText value="raObjectiveTracking-detailProgressObjectiveLabel" />
                    </div>
                </div>
                {editObjectiveButton}
            </div>
        );
    }

    buildObjectiveDetailEndingCard() {
        const {
            goalProgress,
            objectiveEndDate,
            probability,
            hideGoalStatus,
        } = this.props;

        const goalReached = goalProgress >= 1;
        const isGoalNotReached =
            !goalReached &&
            moment(objectiveEndDate).isSameOrBefore(moment(), 'day');
        let probabilityLinkKey =
            goalReached || isGoalNotReached
                ? 'raObjectiveTracking-raObjectiveTracking_detailProbabilityGoalReachedLink'
                : 'raObjectiveTracking-detailProbabilityLink';
        let probabilityLabel = probability;
        if (goalReached) {
            probabilityLabel = RA_GOAL_REACHED;
        } else if (isGoalNotReached) {
            probabilityLabel = RA_GOAL_NOT_REACHED;
        }

        let goalStatusLink;

        if (!hideGoalStatus) {
            goalStatusLink = (
                <div className="ra-objective-details__probability-link">
                    <OKLink onClick={this.handleOnProbabilityAnchorClick}>
                        <FormattedText value={probabilityLinkKey} />
                    </OKLink>
                </div>
            );
        }
        return (
            <div className="ra-objective-details__ending-card">
                <div
                    aria-labeledby="ra-objective-details__ending-date-label"
                    className="ra-objective-details__ending-date"
                >
                    <i className="icon icon-skyline ra-objective-details__goal-flag"></i>
                    {DateHelper.buildFormattedDate(
                        moment(objectiveEndDate),
                        DATE_FORMAT,
                    )}
                </div>
                <div
                    id="ra-objective-details__ending-date-label"
                    className="ra-objective-details__ending-date-label"
                >
                    <FormattedText value="raObjectiveTracking-detailEndingDateLabel" />
                </div>
                <div className="ra-objective-details__probability">
                    <RAProbabilityTag label={probabilityLabel} />
                </div>
                {goalStatusLink}
            </div>
        );
    }

    buildObjectiveDetailProfitabilityCard() {
        const {
            profitabilityAmount,
            profitabilityWeighted,
            initialInvestmentAmount,
        } = this.props;
        return (
            <div className="ra-objective-details__profitability-card">
                <div
                    aria-labeledby="ra-objective-details__profitability-label"
                    className="ra-objective-details__profitability-value"
                >
                    <RAOneLineSymbolAmount
                        className="ra-objective-details__profitability-amount"
                        amountObject={{
                            amount: profitabilityAmount,
                            currency: EURO_TEXT,
                        }}
                    />
                    <span className="ra-objective-details__value-separater">
                        |
                    </span>
                    <span className="ra-objective-details__profitability-percentage">
                        {formatAmount(
                            formatWithoutRoundedPercentage(
                                profitabilityWeighted,
                            ),
                        )}
                        {PERCENTAGE_SYMBOL}
                    </span>
                </div>
                <div
                    id="ra-objective-details__profitability-label"
                    className="ra-objective-details__profitability-label"
                >
                    <FormattedText value="raObjectiveTracking-detailProfitabilityLabel" />
                </div>
                <div
                    aria-labeledby="ra-objective-details__initial-investment-label"
                    className="ra-objective-details__initial-investment-value"
                >
                    <RAOneLineSymbolAmount
                        amountObject={{
                            amount: initialInvestmentAmount,
                            currency: EURO_TEXT,
                        }}
                    />
                </div>
                <div
                    id="ra-objective-details__initial-investment-label"
                    className="ra-objective-details__initial-investment-label"
                >
                    <FormattedText value="raObjectiveTracking-detailInvestmentLabel" />
                </div>
            </div>
        );
    }

    buildObjectiveDetailContributionCard() {
        const { contributionAmount, refundAmount } = this.props;
        return (
            <div className="ra-objective-details__contribution-card">
                <div
                    aria-labeledby="ra-objective-details__contribution-label"
                    className="ra-objective-details__contribution-value"
                >
                    <RAOneLineSymbolAmount
                        amountObject={{
                            amount: contributionAmount,
                            currency: EURO_TEXT,
                        }}
                    />
                </div>
                <div
                    id="ra-objective-details__contribution-label"
                    className="ra-objective-details__contribution-label"
                >
                    <FormattedText value="raObjectiveTracking-detailContributionLabel" />
                </div>
                <div
                    aria-labeledby="ra-objective-details__refund-label"
                    className="ra-objective-details__refund-value"
                >
                    <RAOneLineSymbolAmount
                        amountObject={{
                            amount: refundAmount,
                            currency: EURO_TEXT,
                        }}
                    />
                </div>
                <div
                    id="ra-objective-details__refund-label"
                    className="ra-objective-details__refund-label"
                >
                    <FormattedText value="raObjectiveTracking-detailRefundLabel" />
                </div>
            </div>
        );
    }

    handleCloseButtonClick() {
        recordLink(
            PROC_RA_OBJECTIVE_TRACKING,
            PROC_RA_OBJECTIVE_DETAILS_AMOUNT_MODAL,
            PROC_RA_CLOSE,
        );
        this.props.hideEditAmountModal();
    }

    buildEditGoalAmountModal() {
        const { goalType, immEditAmountModal, resetAmountValue } = this.props;

        const goalImageClass = classNames(
            'ra-objective-details__goal-image',
            `ra-objective-details__goal-image--${goalType.toLowerCase()}`,
        );
        const modalContent = (
            <div className="ra-objective-details__edit-goal-form">
                <label className="ra-objective-details__edit-goal-amount-label">
                    <div className="ra-objective-details__edit-goal-amount-input">
                        <AmountInput
                            amount={immEditAmountModal.get('resetAmount')}
                            callback={resetAmountValue}
                            min={RA_SET_GOAL_MINIMUM_INVESTMENT_AMOUNT}
                            max={RA_SET_GOAL_MAXIMUM_INVESTMENT_AMOUNT}
                            label={formatText(
                                'raObjectiveTracking-raObjectiveTracking_editAmountLabel',
                            )}
                            fluid={true}
                        />
                        <span className="ra-objective-details__edit-goal-amount-currency">
                            {EURO_SYMBOL}
                        </span>
                    </div>
                </label>
            </div>
        );
        const headerContent = <div className={goalImageClass} />;

        return (
            <OKModal
                content={modalContent}
                footerType="action"
                headerAlignment="center"
                headerType="custom"
                headerContent={headerContent}
                isOpen={immEditAmountModal.get('showModal')}
                onCloseCallback={this.handleCloseButtonClick}
                onPrimaryButtonClick={this.handleOnChangeObjectiveAmount}
                primaryButtonText="raObjectiveTracking-raObjectiveTracking_editAmountButton"
                shouldCloseOnOverlayClick={true}
                title="raObjectiveTracking-raObjectiveTracking_editAmountTitle"
                titleAlignment="center"
            />
        );
    }

    buildContent() {
        const { displayStrategyDetail } = this.props;
        let goalAmountModal;

        if (!displayStrategyDetail) {
            goalAmountModal = this.buildEditGoalAmountModal();
        }

        return (
            <div className="row">
                {goalAmountModal}
                <div className="col-xs-12 col-md-5">
                    {this.buildProgressContent()}
                </div>
                <div className="col-xs-12 col-md-7">
                    <div className="ra-objective-details__cards">
                        {this.buildObjectiveDetailEndingCard()}
                        {this.buildObjectiveDetailProfitabilityCard()}
                        {this.buildObjectiveDetailContributionCard()}
                    </div>
                </div>
            </div>
        );
    }

    handleCloseModal() {
        this.setState({
            isModalOpen: false,
        });
    }

    handleOnLinkClick() {
        this.setState({
            isModalOpen: true,
        });
        this.props.calculateLinkClick();
    }

    buildModal() {
        return (
            <RAServiceCostModal
                isModalOpen={this.state.isModalOpen}
                onModalClose={this.handleCloseModal}
                extraColumnForProfit={true}
                titleName="raCustomModel-modelTitleForObjectivo"
            />
        );
    }

    render() {
        const { isFetching, modalLinkText } = this.props;

        let content;
        const modal = this.state.isModalOpen ? this.buildModal() : null;

        if (isFetching) {
            content = <LoadingSection />;
        } else {
            content = this.buildContent();
        }

        const contentFooter = (
            <footer className="row">
                <div className="col-xs-12 ra-objective-details__calculation-info-link">
                    <OKLink
                        onClick={this.handleOnLinkClick}
                        modifier="complementary"
                    >
                        <span>{modalLinkText}</span>
                    </OKLink>
                </div>
            </footer>
        );

        return (
            <ContentBox
                id="raObjectiveDetails"
                title={formatText('raObjectiveTracking-detailTitle')}
                smallTitle={true}
                customClassName="ra-objective-details"
                product="wealth"
            >
                {modal}
                {content}
                {contentFooter}
            </ContentBox>
        );
    }
}

RAObjectiveDetails.propTypes = {
    goalProgress: PropTypes.number.isRequired,
    goalType: PropTypes.string.isRequired,
    actualAmount: PropTypes.number.isRequired,
    displayStrategyDetail: PropTypes.bool,
    modalLinkText: PropTypes.string.isRequired,
    objectiveAmount: PropTypes.number.isRequired,
    objectiveEndDate: PropTypes.string.isRequired,
    onObjectiveAmountChange: PropTypes.func,
    probability: PropTypes.string.isRequired,
    profitabilityAmount: PropTypes.number.isRequired,
    profitabilityWeighted: PropTypes.number.isRequired,
    portfolioId: PropTypes.number.isRequired,
    initialInvestmentAmount: PropTypes.number.isRequired,
    contributionAmount: PropTypes.number.isRequired,
    refundAmount: PropTypes.number.isRequired,
    isFetching: PropTypes.bool.isRequired,
    onProbabilityAnchorClick: PropTypes.func.isRequired,
    immEditAmountModal: PropTypes.object.isRequired,
    showEditAmountModal: PropTypes.func.isRequired,
    hideEditAmountModal: PropTypes.func.isRequired,
    updateObjectiveName: PropTypes.func.isRequired,
    resetAmountValue: PropTypes.func.isRequired,
    productIcon: PropTypes.string,
    productName: PropTypes.string,
    hideGoalStatus: PropTypes.bool,
    calculateLinkClick: PropTypes.func,
};

RAObjectiveDetails.defaultTypes = {
    isFetching: true,
    displayStrategyDetail: false,
    hideGoalStatus: false,
    calculateLinkClick: () => {},
};

module.exports = RAObjectiveDetails;
